import type { AxiosRequestConfig } from "axios";
import qs from "qs";
import { AbortError } from "@/constants/index";
import { useAppStore } from "@/store/app/slice";
import { axiosInstance } from "../../axios-instance";
import type { R } from "../types";
import type {
  IChangeNotificationStatusRequest,
  IChangeNotificationStatusResponse,
  IDeleteNotificationRequest,
  IDeleteNotificationResponse,
  IGetNotificationsCountResponse,
  IGetNotificationsRequest,
  IGetNotificationsResponse,
  IGetUnreadNotificationsRequest,
  IGetUnreadNotificationsResponse,
  IStreamNotificationsEventData,
  IStreamNotificationsRequest,
  IStreamNotificationsResponse,
} from "./types";

interface INotificationsService {
  getAll: (
    p: IGetNotificationsRequest,
    c?: AxiosRequestConfig,
  ) => R<IGetNotificationsResponse>;
  getUnread: (
    p: IGetUnreadNotificationsRequest,
    c?: AxiosRequestConfig,
  ) => R<IGetUnreadNotificationsResponse>;
  getUnreadCount: (c?: AxiosRequestConfig) => R<IGetNotificationsCountResponse>;
  markAsRead: (
    p: IChangeNotificationStatusRequest,
    c?: AxiosRequestConfig,
  ) => R<IChangeNotificationStatusResponse>;
  markAllAsRead: (c?: AxiosRequestConfig) => R<void>;
  delete: (
    p: IDeleteNotificationRequest,
    c?: AxiosRequestConfig,
  ) => R<IDeleteNotificationResponse>;
  deleteAll: (c?: AxiosRequestConfig) => R<void>;
  stream: (p: IStreamNotificationsRequest) => IStreamNotificationsResponse;
}

export class NotificationsService implements INotificationsService {
  async getAll(p: IGetNotificationsRequest): R<IGetNotificationsResponse> {
    try {
      const params = qs.stringify(p);
      return axiosInstance.get(`/api/notifications?${params}`);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async getUnread(
    p: IGetUnreadNotificationsRequest,
  ): R<IGetUnreadNotificationsResponse> {
    try {
      const params = qs.stringify(p);
      return axiosInstance.get(
        `/api/notifications/unread/notifications?${params}`,
      );
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async getUnreadCount(): R<IGetNotificationsCountResponse> {
    try {
      return axiosInstance.get(`/api/notifications/unread/count`);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async markAsRead(p: IChangeNotificationStatusRequest) {
    try {
      return axiosInstance.put(`/api/notifications/mark/${p.id}`);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async markAllAsRead(): R<void> {
    try {
      return axiosInstance.put(`/api/notifications/markAllRead`);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async delete(p: IDeleteNotificationRequest): R<IDeleteNotificationResponse> {
    try {
      return axiosInstance.delete(`/api/notifications/${p.id}`);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async deleteAll(): R<void> {
    try {
      return axiosInstance.delete(`/api/notifications`);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  stream(p: IStreamNotificationsRequest): IStreamNotificationsResponse {
    const abortController = new AbortController();
    const accessToken = useAppStore.getState().accessToken;

    // Extract tenant from URL path (same logic as axios interceptor)
    // Exclude reserved paths that are not tenant names
    const reservedPaths = [
      "admin",
      "auth",
      "login",
      "logout",
      "register",
      "select-tenant",
      "legal",
    ];
    const pathSegments = window.location.pathname.split("/");
    const tenantSegment = pathSegments[1];
    const tenantName =
      tenantSegment && !reservedPaths.includes(tenantSegment.toLowerCase())
        ? decodeURIComponent(tenantSegment)
        : null;

    // Don't attempt to connect if no valid tenant is available
    if (!tenantName) {
      console.warn(
        "Cannot connect to notification stream: No valid tenant in URL path",
      );
      return {
        close: () => {
          // No-op since we didn't actually connect
        },
      };
    }

    fetch(`${axiosInstance.defaults.baseURL}/api/notifications/stream`, {
      headers: {
        Accept: "text/event-stream",
        Authorization: `Bearer ${accessToken}`,
        "X-Tenant-Name": tenantName,
      },
      signal: abortController.signal,
    })
      .then(async (response) => {
        if (!response.ok || !response.body) {
          throw new Error("Failed to connect to notification stream");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          let eventData = "";

          for (const line of lines) {
            if (line.startsWith("data:")) {
              eventData = line.slice(5).trim();
            } else if (line === "" && eventData) {
              try {
                const data: IStreamNotificationsEventData =
                  JSON.parse(eventData);
                p.onUnreadCount(data.unreadCount);
              } catch (e) {
                console.error("Failed to parse SSE data:", e);
              }
              eventData = "";
            }
          }
        }

        p.onClose?.();
      })
      .catch((error) => {
        if (error.name !== AbortError) {
          console.error("Notification stream error:", error);
          p.onClose?.(error);
        }
      });

    return {
      close: () => {
        abortController.abort();
      },
    };
  }
}
